import { useState, useEffect } from 'react';
import { Download, Loader2, ZoomIn, ZoomOut, Maximize2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface SimplePDFPreviewProps {
  file?: File | null;
  url?: string | null;
  className?: string;
  useObject?: boolean;
}

export function SimplePDFPreview({ file, url, className, useObject = true }: SimplePDFPreviewProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    if (file) {
      console.log('PDFPreview: File received:', file.name, file.type, file.size);
      setIsLoading(true);
      setError(null);
      
      // Create blob URL for uploaded file
      const objectUrl = URL.createObjectURL(file);
      console.log('PDFPreview: Created blob URL:', objectUrl);
      setPreviewUrl(objectUrl);
      setIsLoading(false);
      
      return () => {
        console.log('PDFPreview: Cleaning up blob URL');
        URL.revokeObjectURL(objectUrl);
        setPreviewUrl(null);
      };
    } else if (url) {
      console.log('PDFPreview: URL received:', url);
      setPreviewUrl(url);
    } else {
      console.log('PDFPreview: No file or URL');
      setPreviewUrl(null);
    }
  }, [file, url]);

  const handleDownload = () => {
    if (file) {
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  };

  const handleFullScreen = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  };


  if (!file && !url) {
    return (
      <Card className={className}>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <p className="text-slate-500">No document to preview</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`border-red-300 bg-red-50 ${className}`}>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <p className="text-red-600 mb-4">{error}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setError(null)}>
              Retry
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download Instead
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-2" />
          <p className="text-slate-600">Loading PDF...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="p-3 border-b bg-slate-50">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">Bill Document</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setZoom(Math.max(50, zoom - 25))}
              disabled={zoom <= 50}
              className="h-7 w-7 p-0"
            >
              <ZoomOut className="h-3 w-3" />
            </Button>
            <span className="text-xs font-medium min-w-[45px] text-center">
              {zoom}%
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setZoom(Math.min(200, zoom + 25))}
              disabled={zoom >= 200}
              className="h-7 w-7 p-0"
            >
              <ZoomIn className="h-3 w-3" />
            </Button>
            <div className="w-px h-4 bg-slate-300 mx-1" />
            <div className="w-px h-4 bg-slate-300 mx-1" />
            <Button
              variant="outline"
              size="sm"
              onClick={handleFullScreen}
              className="h-7 w-7 p-0"
            >
              <Maximize2 className="h-3 w-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="h-7 px-2"
            >
              <Download className="h-3 w-3 mr-1" />
              <span className="text-xs">Download</span>
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="relative bg-slate-100" style={{ height: '650px' }}>
          <div className="overflow-auto h-full">
            <div
              style={{
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top left',
                width: `${100 / zoom}%`,
                height: `${100 / zoom}%`,
              }}
            >
              {useObject ? (
                <object
                  data={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                  className="border-0"
                  onError={() => {
                    console.log('PDFPreview: Object tag failed to load');
                    setError('PDF failed to load with object tag');
                  }}
                  onLoad={() => console.log('PDFPreview: Object tag loaded successfully')}
                >
                  <div className="flex flex-col items-center justify-center h-full py-16">
                    <FileText className="h-16 w-16 text-slate-400 mb-4" />
                    <p className="text-slate-600 mb-4">PDF preview not supported</p>
                    <Button variant="outline" size="sm" onClick={handleDownload}>
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </object>
              ) : (
                <iframe
                  src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                  className="w-full h-full border-0"
                  title="PDF Preview"
                  onError={() => {
                    console.log('PDFPreview: Iframe failed to load');
                    setError('PDF failed to load with iframe');
                  }}
                  onLoad={() => console.log('PDFPreview: Iframe loaded successfully')}
                  style={{ minHeight: '800px' }}
                />
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
